<template>
    <div>
        <TagSelector mainHeading="" optionName="users"
            :availableOptionsHeading="availableOptionsHeading"
            :selectedOptionsHeading="selectedOptionsHeading"
            :showDefaultMessage="false"
            :initial-skills="availableActivities" v-model:interests="userActivities"
            :onSave="readOnly ? null : handleSaveActivities"
            :loading="loading" :isSaving="submitting" :disabled="readOnly" />
    </div>
</template>

<script setup>
import { onMounted, ref, computed } from "vue";
import { useStore } from "vuex";
import TagSelector from "@/components/common/TagSelector.vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toast-notification";

defineProps({
    readOnly: { type: Boolean, default: false }
});

const store = useStore();
const route = useRoute();
const toast = useToast();

// ✅ Get IDs from route params
const instanceWorkgroupId = route.params.instanceworkgroupid;

// ✅ Reactive state
const availableActivities = ref([]); // all users with skill
const userActivities = ref([]); // scheduled volunteers
const scheduledPersonsData = ref(null);

const loading = computed(() => store.getters.instanceWorkgroupLoading);
const submitting = ref(false);

// ✅ Dynamic headings based on API response
const availableOptionsHeading = computed(() => {
    if (!scheduledPersonsData.value) return "All Users with skill";
    const skillName = scheduledPersonsData.value.skill?.name || "";
    return `All Users with skill ${skillName}`;
});

const selectedOptionsHeading = computed(() => {
    if (!scheduledPersonsData.value) return "Scheduled volunteers";
    const workgroupName = scheduledPersonsData.value.workgroup_name || "";
    const instanceDate = scheduledPersonsData.value.instance_date || "";
    return `Scheduled volunteers: ${workgroupName} (${instanceDate})`;
});

// ✅ Load scheduled persons
const loadScheduledPersons = async () => {
    try {
        const data = await store.dispatch("getScheduledPersons", instanceWorkgroupId);
        
        scheduledPersonsData.value = data;

        // Map all_users_with_skill to available options
        const allUsersWithSkill = data?.all_users_with_skill || [];
        availableActivities.value = allUsersWithSkill.map((user) => ({
            id: user.id,
            name: user.full_name || `${user.firstname} ${user.lastname}`,
            description: user.email || user.title || "",
        }));

        // Map scheduled_volunteers to selected options
        const scheduledVolunteers = data?.scheduled_volunteers || [];
        userActivities.value = scheduledVolunteers.map((user) => ({
            id: user.id,
            name: user.full_name || `${user.firstname} ${user.lastname}`,
            description: user.email || user.title || "",
        }));
    } catch (error) {
        console.error("Failed to load scheduled persons:", error);
        toast.error("Failed to load scheduled persons", { position: "top-right" });
    }
};

// ✅ Save handler for scheduled persons
const handleSaveActivities = async (selected) => {
    submitting.value = true;
    try {
        const userIds = selected.map((x) => x.id);

        await store.dispatch("saveScheduledPersons", {
            aworkgroupinstanceid: instanceWorkgroupId,
            userIdsArray: userIds,
        });

        toast.success(`Successfully saved ${selected.length} volunteer(s)`, {
            position: "top-right"
        });

        // Reload data after successful save
        await loadScheduledPersons();
    } catch (error) {
        console.error("Failed to save scheduled persons:", error);
        toast.error("Failed to save scheduled persons", { position: "top-right" });
    } finally {
        submitting.value = false;
    }
};

onMounted(async () => {
    await loadScheduledPersons();
});
</script>